import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { JournalistProfileSheet } from "@/components/MediaRegistry/JournalistProfileSheet";
import { JournalistComparisonTab } from "@/components/MediaRegistry/JournalistComparisonTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { toast } from "sonner";
import { Plus, Search, Newspaper, UserRound, Activity, Ban, Globe, Star, Trophy, Upload, Loader2, ArrowRightLeft, Clock, Check, X, ShieldCheck } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

type Journalist = {
  id: string; org_id: string;
  first_name: string; last_name: string;
  email: string | null; phone: string | null; whatsapp: string | null;
  nationality: string | null; gender: string;
  languages: string[]; beats: string[];
  seniority: string; status: string;
  bio: string | null; photo_url: string | null;
  blacklist_reason: string | null; consent_marketing: boolean;
  position: string | null; next_of_kin: string | null; emergency_contact: string | null;
  press_card_status: string | null; outlet_id: string | null; verification_id: string | null;
};

type Outlet = {
  id: string; org_id: string;
  name: string; type: string; tier: string;
  country: string | null; city: string | null;
  languages: string[]; website: string | null; logo_url: string | null;
  reach_estimate: number | null; status: string; notes: string | null;
};

type Engagement = {
  id: string; org_id: string;
  journalist_id: string; engagement_type: string;
  summary: string | null; occurred_on: string;
};

const SENIORITY = ["junior","reporter","senior","bureau_chief","editor","freelance"];
const J_STATUS = ["active","inactive","blacklisted"];
const GENDER = ["female","male","other","undisclosed"];
const OUTLET_TYPES = ["newspaper","magazine","tv","radio","online","news_agency","blog","podcast","freelance_collective"];
const TIERS = ["community","regional","national","international"];
const ENGAGEMENT_TYPES = ["event_attendance","interview","accreditation","award_nomination","press_release","meeting","other"];
const PRESS_CARD_STATUS = ["not_submitted","valid","damaged","expired"];

const splitCsv = (s: string) => s.split(",").map(x => x.trim()).filter(Boolean);
const joinCsv = (a: string[] | null | undefined) => (a ?? []).join(", ");

export default function MediaRegistry() {
  const { orgId, can, loading: permLoading } = useOrgPermissions();
  const canEdit = can("media_registry", "edit");
  const canFull = can("media_registry", "full");
  const [searchParams, setSearchParams] = useSearchParams();
  const openJournalistId = searchParams.get("journalist");

  const [journalists, setJournalists] = useState<Journalist[]>([]);
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [engagements, setEngagements] = useState<Engagement[]>([]);
  const [loading, setLoading] = useState(true);

  const [jSearch, setJSearch] = useState("");
  const [oSearch, setOSearch] = useState("");
  const [jCountry, setJCountry] = useState("all");
  const [orgLogoUrl, setOrgLogoUrl] = useState<string | null>(null);
  const [detailOutlet, setDetailOutlet] = useState<Outlet | null>(null);

  const refresh = async () => {
    if (!orgId) return;
    setLoading(true);
    const [j, o, e, org] = await Promise.all([
      // @ts-ignore new tables not yet in generated types
      supabase.from("journalists" as any).select("*").eq("org_id", orgId).order("last_name"),
      // @ts-ignore
      supabase.from("media_outlets" as any).select("*").eq("org_id", orgId).order("name"),
      // @ts-ignore
      supabase.from("journalist_engagements" as any).select("*").eq("org_id", orgId).order("occurred_on", { ascending: false }).limit(200),
      // @ts-ignore
      supabase.from("organizations" as any).select("logo_url").eq("id", orgId).maybeSingle(),
    ]);
    setJournalists((j.data as any) ?? []);
    setOutlets((o.data as any) ?? []);
    setEngagements((e.data as any) ?? []);
    setOrgLogoUrl((org.data as any)?.logo_url ?? null);
    setLoading(false);
  };

  useEffect(() => { if (orgId) refresh(); }, [orgId]);

  const journalistById = useMemo(() => {
    const m = new Map<string, Journalist>();
    journalists.forEach(j => m.set(j.id, j));
    return m;
  }, [journalists]);

  const outletById = useMemo(() => {
    const m = new Map<string, Outlet>();
    outlets.forEach(o => m.set(o.id, o));
    return m;
  }, [outlets]);

  const journalistCountries = useMemo(() => {
    const set = new Set<string>();
    journalists.forEach(j => { if (j.nationality) set.add(j.nationality); });
    return Array.from(set).sort();
  }, [journalists]);

  const filteredJournalists = useMemo(() => {
    const q = jSearch.toLowerCase().trim();
    return journalists.filter(j => {
      const matchesCountry = jCountry === "all" || j.nationality === jCountry;
      const matchesSearch = !q ||
        `${j.first_name} ${j.last_name} ${j.email ?? ""} ${j.nationality ?? ""} ${j.beats.join(" ")}`.toLowerCase().includes(q);
      return matchesCountry && matchesSearch;
    });
  }, [journalists, jSearch, jCountry]);

  const filteredOutlets = useMemo(() => {
    const q = oSearch.toLowerCase().trim();
    if (!q) return outlets;
    return outlets.filter(o =>
      `${o.name} ${o.country ?? ""} ${o.city ?? ""} ${o.type}`.toLowerCase().includes(q)
    );
  }, [outlets, oSearch]);

  const journalistStatusCounts = useMemo(() => {
    const c = { active: 0, inactive: 0, blacklisted: 0 };
    journalists.forEach(j => { if (j.status in c) (c as any)[j.status]++; });
    return c;
  }, [journalists]);

  const pressCardCounts = useMemo(() => {
    const c: Record<string, number> = { valid: 0, expired: 0, damaged: 0, not_submitted: 0 };
    journalists.forEach(j => {
      const s = j.press_card_status ?? "not_submitted";
      c[s] = (c[s] ?? 0) + 1;
    });
    return c;
  }, [journalists]);

  const outletStatusCounts = useMemo(() => {
    const c = { active: 0, archived: 0 };
    outlets.forEach(o => { if (o.status in c) (c as any)[o.status]++; });
    return c;
  }, [outlets]);

  const { data: pendingOutletRequests = 0 } = useQuery({
    queryKey: ["outlet-change-pending-count", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { count } = await (supabase
        .from("outlet_change_requests" as any)
        .select("id", { count: "exact", head: true })
        .eq("org_id", orgId)
        .eq("status", "pending") as any);
      return count ?? 0;
    },
  });

  if (permLoading || loading) {
    return <div className="text-muted-foreground">Loading media registry…</div>;
  }

  if (!can("media_registry", "view")) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-justify text-muted-foreground">You don't have access to the Media Registry.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Media Registry</h2>
          <p className="text-justify text-muted-foreground">Journalists, outlets, and engagement history</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          icon={UserRound} label="Journalists" value={journalists.length}
          breakdown={[
            { label: "Active", value: journalistStatusCounts.active, className: "bg-success/15 text-success" },
            { label: "Inactive", value: journalistStatusCounts.inactive },
            { label: "Blacklisted", value: journalistStatusCounts.blacklisted, className: "bg-destructive/15 text-destructive" },
          ]}
        />
        <StatCard
          icon={ShieldCheck} label="Accreditations (press cards)" value={pressCardCounts.valid + pressCardCounts.expired + pressCardCounts.damaged}
          breakdown={[
            { label: "Valid", value: pressCardCounts.valid, className: "bg-success/15 text-success" },
            { label: "Expired", value: pressCardCounts.expired, className: "bg-destructive/15 text-destructive" },
            { label: "Damaged", value: pressCardCounts.damaged, className: "bg-warning/15 text-warning" },
            { label: "Not submitted", value: pressCardCounts.not_submitted },
          ]}
        />
        <StatCard
          icon={Newspaper} label="Outlets" value={outlets.length}
          breakdown={[
            { label: "Active", value: outletStatusCounts.active, className: "bg-success/15 text-success" },
            { label: "Archived", value: outletStatusCounts.archived },
          ]}
        />
        <StatCard icon={Activity} label="Engagements logged" value={engagements.length} />
      </div>

      <Tabs defaultValue="journalists" className="space-y-4">
        <TabsList>
          <TabsTrigger value="journalists">Journalists</TabsTrigger>
          <TabsTrigger value="outlets">Outlets</TabsTrigger>
          <TabsTrigger value="engagements">Engagements</TabsTrigger>
          <TabsTrigger value="compare">Compare</TabsTrigger>
          <TabsTrigger value="discover">Discover</TabsTrigger>
          <TabsTrigger value="outlet-requests" className="gap-1.5">
            Outlet requests
            {pendingOutletRequests > 0 && (
              <Badge className="h-4 min-w-4 px-1 text-[10px] bg-amber-500 text-white">{pendingOutletRequests}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* JOURNALISTS */}
        <TabsContent value="journalists" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search by name, email, nationality, beat…"
                value={jSearch} onChange={(e) => setJSearch(e.target.value)} />
            </div>
            <Select value={jCountry} onValueChange={setJCountry}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="All countries" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All countries</SelectItem>
                {journalistCountries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {canEdit && (
              <JournalistDialog orgId={orgId!} outlets={outlets} onSaved={refresh}>
                <Button><Plus className="w-4 h-4 mr-2" /> Add journalist</Button>
              </JournalistDialog>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {filteredJournalists.map(j => (
              <Card
                key={j.id}
                className="hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
                onClick={() => setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set("journalist", j.id);
                  return next;
                })}
              >
                <div className="relative aspect-square w-full bg-muted">
                  {j.photo_url ? (
                    <img src={j.photo_url} alt="" className="w-full h-full object-cover" />
                  ) : orgLogoUrl ? (
                    <img src={orgLogoUrl} alt="" className="w-full h-full object-contain p-8 bg-muted" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl font-semibold text-muted-foreground">
                      {j.first_name?.[0]}{j.last_name?.[0]}
                    </div>
                  )}
                  <div className="absolute top-2 right-2">
                    <StatusBadge status={j.status} />
                  </div>
                </div>
                <CardHeader className="pb-3">
                  <div className="min-w-0">
                    <CardTitle className="text-base truncate">{j.first_name} {j.last_name}</CardTitle>
                    <p className="text-justify text-xs text-muted-foreground mt-0.5 capitalize truncate">
                      {j.seniority.replace("_", " ")} {j.nationality ? `• ${j.nationality}` : ""}
                    </p>
                    {j.verification_id && (
                      <p className="text-[10px] font-mono text-muted-foreground mt-0.5">{j.verification_id}</p>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {j.email && <div className="truncate">{j.email}</div>}
                  {j.phone && <div className="text-muted-foreground">{j.phone}</div>}
                  {j.beats.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {j.beats.slice(0, 4).map(b => (
                        <Badge key={b} variant="secondary" className="text-[10px]">{b}</Badge>
                      ))}
                    </div>
                  )}
                  {(canEdit || canFull) && (
                    <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
                      {canEdit && (
                        <JournalistDialog orgId={orgId!} journalist={j} outlets={outlets} onSaved={refresh}>
                          <Button size="sm" variant="outline">Edit</Button>
                        </JournalistDialog>
                      )}
                      {canFull && (
                        <Button size="sm" variant="ghost" onClick={async () => {
                          if (!confirm(`Delete ${j.first_name} ${j.last_name}?`)) return;
                          // @ts-ignore
                          const { error } = await supabase.from("journalists" as any).delete().eq("id", j.id);
                          if (error) toast.error(error.message); else { toast.success("Deleted"); refresh(); }
                        }}>Delete</Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            {filteredJournalists.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="pt-6 text-center text-muted-foreground">
                  No journalists yet. {canEdit && "Click \"Add journalist\" to get started."}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* OUTLETS */}
        <TabsContent value="outlets" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search by name, country, type…"
                value={oSearch} onChange={(e) => setOSearch(e.target.value)} />
            </div>
            {canEdit && (
              <OutletDialog orgId={orgId!} onSaved={refresh}>
                <Button><Plus className="w-4 h-4 mr-2" /> Add outlet</Button>
              </OutletDialog>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {filteredOutlets.map(o => {
              const repCount = journalists.filter(j => j.outlet_id === o.id).length;
              return (
              <Card key={o.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setDetailOutlet(o)}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-3 min-w-0">
                      {o.logo_url ? (
                        <img src={o.logo_url} alt="" className="w-12 h-12 rounded-lg object-contain shrink-0 border border-border bg-white p-1" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center shrink-0 text-muted-foreground">
                          <Newspaper className="w-5 h-5" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <CardTitle className="text-base truncate">{o.name}</CardTitle>
                        <p className="text-justify text-xs text-muted-foreground mt-0.5 capitalize truncate">
                          {o.type.replace("_", " ")} • {o.tier}
                        </p>
                      </div>
                    </div>
                    <StatusBadge status={o.status} />
                  </div>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  {(o.city || o.country) && (
                    <div className="text-muted-foreground">
                      {[o.city, o.country].filter(Boolean).join(", ")}
                    </div>
                  )}
                  {o.website && <a href={o.website} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-primary hover:underline truncate block">{o.website}</a>}
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1">
                    <UserRound className="w-3.5 h-3.5" /> {repCount} accredited representative{repCount === 1 ? "" : "s"}
                  </div>
                  {o.languages.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {o.languages.map(l => <Badge key={l} variant="outline" className="text-[10px]">{l}</Badge>)}
                    </div>
                  )}
                  {(canEdit || canFull) && (
                    <div className="flex gap-2 pt-2" onClick={e => e.stopPropagation()}>
                      {canEdit && (
                        <OutletDialog orgId={orgId!} outlet={o} onSaved={refresh}>
                          <Button size="sm" variant="outline">Edit</Button>
                        </OutletDialog>
                      )}
                      {canFull && (
                        <Button size="sm" variant="ghost" onClick={async () => {
                          if (!confirm(`Delete ${o.name}?`)) return;
                          // @ts-ignore
                          const { error } = await supabase.from("media_outlets" as any).delete().eq("id", o.id);
                          if (error) toast.error(error.message); else { toast.success("Deleted"); refresh(); }
                        }}>Delete</Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              );
            })}
            {filteredOutlets.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="pt-6 text-center text-muted-foreground">
                  No outlets yet. {canEdit && "Click \"Add outlet\" to get started."}
                </CardContent>
              </Card>
            )}
          </div>

          <OutletDetailSheet
            outlet={detailOutlet}
            open={!!detailOutlet}
            onOpenChange={(o) => !o && setDetailOutlet(null)}
            journalists={journalists}
          />
        </TabsContent>

        {/* ENGAGEMENTS */}
        <TabsContent value="engagements" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-justify text-sm text-muted-foreground">Last 200 interactions</p>
            {canEdit && (
              <EngagementDialog orgId={orgId!} journalists={journalists} onSaved={refresh}>
                <Button><Plus className="w-4 h-4 mr-2" /> Log engagement</Button>
              </EngagementDialog>
            )}
          </div>
          <Card>
            <CardContent className="pt-6">
              {engagements.length === 0 ? (
                <p className="text-justify text-center text-muted-foreground">No engagements logged yet.</p>
              ) : (
                <div className="space-y-3">
                  {engagements.map(e => {
                    const j = journalistById.get(e.journalist_id);
                    return (
                      <div key={e.id} className="flex items-start gap-3 pb-3 border-b last:border-b-0">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <Activity className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-medium">{j ? `${j.first_name} ${j.last_name}` : "Unknown"}</span>
                            <Badge variant="outline" className="text-[10px] capitalize">
                              {e.engagement_type.replace(/_/g, " ")}
                            </Badge>
                            <span className="text-xs text-muted-foreground ml-auto">{e.occurred_on}</span>
                          </div>
                          {e.summary && <p className="text-justify text-sm text-muted-foreground mt-1">{e.summary}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Discover tab ─────────────────────────────────────────── */}
        <TabsContent value="compare" className="space-y-4">
          <JournalistComparisonTab orgId={orgId!} journalists={journalists} engagements={engagements} />
        </TabsContent>

        <TabsContent value="discover" className="space-y-4">
          <DiscoverTab canEdit={canEdit} orgId={orgId} outlets={outlets} onImported={refresh} />
        </TabsContent>

        <TabsContent value="outlet-requests" className="space-y-4">
          <OutletRequestsTab orgId={orgId} canEdit={canEdit} journalistById={journalistById} outletById={outletById} />
        </TabsContent>
      </Tabs>

      <JournalistProfileSheet
        journalistId={openJournalistId}
        orgId={orgId!}
        onClose={() => setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete("journalist");
          return next;
        })}
      />
    </div>
  );
}

function StatCard({
  icon: Icon, label, value, breakdown,
}: {
  icon: any; label: string; value: number;
  breakdown?: { label: string; value: number; className?: string }[];
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Icon className="w-6 h-6 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-justify text-sm text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
        </div>
        {breakdown && breakdown.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t">
            {breakdown.map(b => (
              <span key={b.label} className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${b.className ?? "bg-muted text-muted-foreground"}`}>
                {b.label}: {b.value}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant = status === "active" ? "default" : status === "blacklisted" ? "destructive" : "secondary";
  return (
    <Badge variant={variant as any} className="capitalize text-[10px]">
      {status === "blacklisted" && <Ban className="w-3 h-3 mr-1" />}
      {status}
    </Badge>
  );
}

// =====================================================================
// Dialogs
// =====================================================================

function JournalistDialog({
  orgId, journalist, outlets, onSaved, children,
}: {
  orgId: string; journalist?: Journalist; outlets: Outlet[]; onSaved: () => void; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const [form, setForm] = useState({
    first_name: journalist?.first_name ?? "",
    last_name: journalist?.last_name ?? "",
    email: journalist?.email ?? "",
    phone: journalist?.phone ?? "",
    whatsapp: journalist?.whatsapp ?? "",
    nationality: journalist?.nationality ?? "",
    gender: journalist?.gender ?? "undisclosed",
    languages: joinCsv(journalist?.languages),
    beats: joinCsv(journalist?.beats),
    seniority: journalist?.seniority ?? "reporter",
    status: journalist?.status ?? "active",
    bio: journalist?.bio ?? "",
    blacklist_reason: journalist?.blacklist_reason ?? "",
    consent_marketing: journalist?.consent_marketing ?? false,
    position: journalist?.position ?? "",
    next_of_kin: journalist?.next_of_kin ?? "",
    emergency_contact: journalist?.emergency_contact ?? "",
    press_card_status: journalist?.press_card_status ?? "",
    outlet_id: journalist?.outlet_id ?? "",
    photo_url: journalist?.photo_url ?? "",
  });

  async function handlePhotoUpload(file: File) {
    setUploadingPhoto(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${orgId}/${(journalist?.id ?? crypto.randomUUID())}-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("journalist-avatars")
        .upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from("journalist-avatars").getPublicUrl(path);
      setForm((f) => ({ ...f, photo_url: data.publicUrl }));
      toast.success("Photo uploaded");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUploadingPhoto(false);
    }
  }

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("First and last name are required");
      return;
    }
    if (!form.outlet_id) {
      toast.error("Every journalist must be assigned to an outlet");
      return;
    }
    setSaving(true);
    const payload = {
      org_id: orgId,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      whatsapp: form.whatsapp.trim() || null,
      nationality: form.nationality.trim() || null,
      gender: form.gender,
      languages: splitCsv(form.languages),
      beats: splitCsv(form.beats),
      seniority: form.seniority,
      status: form.status,
      bio: form.bio.trim() || null,
      blacklist_reason: form.status === "blacklisted" ? (form.blacklist_reason.trim() || null) : null,
      consent_marketing: form.consent_marketing,
      position: form.position.trim() || null,
      next_of_kin: form.next_of_kin.trim() || null,
      emergency_contact: form.emergency_contact.trim() || null,
      press_card_status: form.press_card_status || null,
      outlet_id: form.outlet_id || null,
      photo_url: form.photo_url || null,
    };
    // @ts-ignore
    const q = journalist
      ? supabase.from("journalists" as any).update(payload).eq("id", journalist.id)
      : supabase.from("journalists" as any).insert(payload);
    const { data: saved, error } = await q.select("id").single();
    if (error) {
      setSaving(false);
      if (error.code === "23505") {
        toast.error(
          error.message.includes("verification_id")
            ? "That verification ID is already assigned to another journalist. Each journalist must be unique — no duplicate accounts."
            : "A journalist with this email already exists in your registry. Each journalist must be unique — no duplicate accounts."
        );
      } else {
        toast.error(error.message);
      }
      return;
    }

    // Keep the primary affiliation row in sync with the selected outlet.
    // Close out the previous primary affiliation (ended_on) rather than
    // deleting it, so outlet cards can show correspondent history.
    const journalistId = journalist?.id ?? (saved as any)?.id;
    if (journalistId && form.outlet_id !== (journalist?.outlet_id ?? "")) {
      await supabase.from("journalist_affiliations" as any)
        .update({ ended_on: new Date().toISOString().slice(0, 10) })
        .eq("journalist_id", journalistId).eq("is_primary", true).is("ended_on", null);
      if (form.outlet_id) {
        await supabase.from("journalist_affiliations" as any).insert({
          org_id: orgId,
          journalist_id: journalistId,
          outlet_id: form.outlet_id,
          is_primary: true,
          started_on: new Date().toISOString().slice(0, 10),
        });
      }
    }

    setSaving(false);
    toast.success(journalist ? "Updated" : "Created");
    setOpen(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{journalist ? "Edit journalist" : "New journalist"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Photo" className="col-span-2">
            <div className="flex items-center gap-3">
              <Avatar className="w-14 h-14">
                <AvatarImage src={form.photo_url || undefined} />
                <AvatarFallback>
                  {form.first_name?.[0]}
                  {form.last_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handlePhotoUpload(f);
                  e.target.value = "";
                }}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={uploadingPhoto}
                onClick={() => photoInputRef.current?.click()}
              >
                {uploadingPhoto ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                )}
                {form.photo_url ? "Replace photo" : "Upload photo"}
              </Button>
            </div>
          </Field>
          <Field label="First name *"><Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} /></Field>
          <Field label="Last name *"><Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="WhatsApp"><Input value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} /></Field>
          <Field label="Nationality"><Input value={form.nationality} onChange={e => setForm({ ...form, nationality: e.target.value })} /></Field>
          <Field label="Gender">
            <Select value={form.gender} onValueChange={v => setForm({ ...form, gender: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{GENDER.map(g => <SelectItem key={g} value={g} className="capitalize">{g}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Seniority">
            <Select value={form.seniority} onValueChange={v => setForm({ ...form, seniority: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{SENIORITY.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Languages (comma-separated)"><Input value={form.languages} onChange={e => setForm({ ...form, languages: e.target.value })} placeholder="en, fr, pt" /></Field>
          <Field label="Beats (comma-separated)"><Input value={form.beats} onChange={e => setForm({ ...form, beats: e.target.value })} placeholder="politics, economy, security" /></Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{J_STATUS.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Marketing consent">
            <div className="flex items-center h-10">
              <input type="checkbox" checked={form.consent_marketing} onChange={e => setForm({ ...form, consent_marketing: e.target.checked })} />
              <span className="ml-2 text-sm text-muted-foreground">Opted in to comms</span>
            </div>
          </Field>
          {form.status === "blacklisted" && (
            <Field label="Blacklist reason" className="col-span-2">
              <Textarea value={form.blacklist_reason} onChange={e => setForm({ ...form, blacklist_reason: e.target.value })} />
            </Field>
          )}
          <Field label="Position / Designation"><Input value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} /></Field>
          <Field label="Outlet *">
            <Select value={form.outlet_id} onValueChange={v => setForm({ ...form, outlet_id: v })}>
              <SelectTrigger><SelectValue placeholder="Select outlet…" /></SelectTrigger>
              <SelectContent>
                {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {outlets.length === 0 && (
              <p className="text-justify text-xs text-destructive mt-1">Add an outlet first before creating journalists.</p>
            )}
          </Field>
          <Field label="Press card status">
            <Select value={form.press_card_status || "__none"} onValueChange={v => setForm({ ...form, press_card_status: v === "__none" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="Not set" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Not set</SelectItem>
                {PRESS_CARD_STATUS.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Next of kin"><Input value={form.next_of_kin} onChange={e => setForm({ ...form, next_of_kin: e.target.value })} /></Field>
          <Field label="Emergency contact"><Input value={form.emergency_contact} onChange={e => setForm({ ...form, emergency_contact: e.target.value })} /></Field>
          <Field label="Bio" className="col-span-2">
            <Textarea rows={3} value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type Correspondent = {
  id: string; is_primary: boolean; started_on: string | null; ended_on: string | null;
  journalists: { id: string; first_name: string; last_name: string; photo_url: string | null; position: string | null; status: string } | null;
};

function OutletDetailSheet({
  outlet, open, onOpenChange, journalists,
}: {
  outlet: Outlet | null; open: boolean; onOpenChange: (open: boolean) => void; journalists: Journalist[];
}) {
  const [correspondents, setCorrespondents] = useState<Correspondent[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!outlet || !open) return;
    setLoading(true);
    // @ts-ignore
    supabase.from("journalist_affiliations" as any)
      .select("id, is_primary, started_on, ended_on, journalists(id, first_name, last_name, photo_url, position, status)")
      .eq("outlet_id", outlet.id)
      .order("started_on", { ascending: false })
      .then(({ data, error }: any) => {
        if (!error) setCorrespondents((data as any) ?? []);
        setLoading(false);
      });
  }, [outlet, open]);

  if (!outlet) return null;

  const current = correspondents.filter(c => !c.ended_on);
  const past = correspondents.filter(c => !!c.ended_on);
  const activeReps = journalists.filter(j => j.outlet_id === outlet.id);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[440px] overflow-y-auto">
        <SheetHeader className="mb-4">
          <div className="flex items-center gap-3">
            {outlet.logo_url ? (
              <img src={outlet.logo_url} alt="" className="w-12 h-12 rounded-lg object-contain border border-border bg-white p-1" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center text-muted-foreground">
                <Newspaper className="w-5 h-5" />
              </div>
            )}
            <div>
              <SheetTitle>{outlet.name}</SheetTitle>
              <p className="text-xs text-muted-foreground capitalize mt-0.5">
                {outlet.type.replace("_", " ")} • {outlet.tier}
              </p>
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {(outlet.city || outlet.country) && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Location</div>
                <div>{[outlet.city, outlet.country].filter(Boolean).join(", ")}</div>
              </div>
            )}
            {outlet.website && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Website</div>
                <a href={outlet.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate block">{outlet.website}</a>
              </div>
            )}
            {outlet.reach_estimate != null && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Estimated reach</div>
                <div>{outlet.reach_estimate.toLocaleString()}</div>
              </div>
            )}
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Accredited representatives</div>
              <div>{activeReps.length}</div>
            </div>
          </div>

          {outlet.notes && (
            <p className="text-sm text-muted-foreground text-justify">{outlet.notes}</p>
          )}

          <div>
            <h4 className="text-sm font-semibold mb-2">Current correspondents ({current.length})</h4>
            {loading ? (
              <p className="text-xs text-muted-foreground">Loading…</p>
            ) : current.length === 0 ? (
              <p className="text-xs text-muted-foreground">No current correspondents on record.</p>
            ) : (
              <div className="space-y-2">
                {current.map(c => <CorrespondentRow key={c.id} c={c} />)}
              </div>
            )}
          </div>

          {past.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Past correspondents ({past.length})</h4>
              <div className="space-y-2">
                {past.map(c => <CorrespondentRow key={c.id} c={c} />)}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CorrespondentRow({ c }: { c: Correspondent }) {
  const j = c.journalists;
  if (!j) return null;
  return (
    <div className="flex items-center gap-2.5 p-2 rounded-lg bg-muted/50">
      {j.photo_url ? (
        <img src={j.photo_url} alt="" className="w-9 h-9 rounded-lg object-cover shrink-0" />
      ) : (
        <div className="w-9 h-9 rounded-lg bg-background border flex items-center justify-center shrink-0 text-xs font-semibold text-muted-foreground">
          {j.first_name?.[0]}{j.last_name?.[0]}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{j.first_name} {j.last_name}</p>
        <p className="text-[11px] text-muted-foreground truncate">
          {c.started_on ? new Date(c.started_on).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
          {c.ended_on ? ` – ${new Date(c.ended_on).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}` : " – present"}
        </p>
      </div>
      {c.is_primary && <Badge variant="outline" className="text-[10px] shrink-0">Primary</Badge>}
    </div>
  );
}

function OutletDialog({
  orgId, outlet, onSaved, children,
}: {
  orgId: string; outlet?: Outlet; onSaved: () => void; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: outlet?.name ?? "",
    type: outlet?.type ?? "newspaper",
    tier: outlet?.tier ?? "national",
    country: outlet?.country ?? "",
    city: outlet?.city ?? "",
    languages: joinCsv(outlet?.languages),
    website: outlet?.website ?? "",
    logo_url: outlet?.logo_url ?? "",
    reach_estimate: outlet?.reach_estimate?.toString() ?? "",
    status: outlet?.status ?? "active",
    notes: outlet?.notes ?? "",
  });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    const payload = {
      org_id: orgId,
      name: form.name.trim(),
      type: form.type,
      tier: form.tier,
      country: form.country.trim() || null,
      city: form.city.trim() || null,
      languages: splitCsv(form.languages),
      website: form.website.trim() || null,
      logo_url: form.logo_url.trim() || null,
      reach_estimate: form.reach_estimate ? parseInt(form.reach_estimate, 10) : null,
      status: form.status,
      notes: form.notes.trim() || null,
    };
    // @ts-ignore
    const q = outlet
      ? supabase.from("media_outlets" as any).update(payload).eq("id", outlet.id)
      : supabase.from("media_outlets" as any).insert(payload);
    const { error } = await q;
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(outlet ? "Updated" : "Created");
    setOpen(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{outlet ? "Edit outlet" : "New outlet"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name *" className="col-span-2"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Type">
            <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{OUTLET_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Tier">
            <Select value={form.tier} onValueChange={v => setForm({ ...form, tier: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TIERS.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Country"><Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} /></Field>
          <Field label="City"><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></Field>
          <Field label="Languages (comma-separated)"><Input value={form.languages} onChange={e => setForm({ ...form, languages: e.target.value })} placeholder="en, fr" /></Field>
          <Field label="Website"><Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://…" /></Field>
          <Field label="Logo URL" className="col-span-2"><Input value={form.logo_url} onChange={e => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…/logo.png" /></Field>
          <Field label="Reach estimate"><Input type="number" value={form.reach_estimate} onChange={e => setForm({ ...form, reach_estimate: e.target.value })} /></Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Notes" className="col-span-2">
            <Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EngagementDialog({
  orgId, journalists, onSaved, children,
}: {
  orgId: string; journalists: Journalist[]; onSaved: () => void; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    journalist_id: "",
    engagement_type: "meeting",
    summary: "",
    occurred_on: new Date().toISOString().slice(0, 10),
  });

  const save = async () => {
    if (!form.journalist_id) { toast.error("Pick a journalist"); return; }
    setSaving(true);
    const { data: userData } = await supabase.auth.getUser();
    // @ts-ignore
    const { error } = await supabase.from("journalist_engagements" as any).insert({
      org_id: orgId,
      journalist_id: form.journalist_id,
      engagement_type: form.engagement_type,
      summary: form.summary.trim() || null,
      occurred_on: form.occurred_on,
      created_by: userData.user?.id ?? null,
    });
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Logged");
    setOpen(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Log engagement</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Journalist *">
            <Select value={form.journalist_id} onValueChange={v => setForm({ ...form, journalist_id: v })}>
              <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {journalists.map(j => (
                  <SelectItem key={j.id} value={j.id}>{j.first_name} {j.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Type">
            <Select value={form.engagement_type} onValueChange={v => setForm({ ...form, engagement_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ENGAGEMENT_TYPES.map(t => (
                  <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Date">
            <Input type="date" value={form.occurred_on} onChange={e => setForm({ ...form, occurred_on: e.target.value })} />
          </Field>
          <Field label="Summary">
            <Textarea rows={3} value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =====================================================================
// Outlet change requests
// =====================================================================

type OutletChangeRequest = {
  id: string; org_id: string; journalist_id: string;
  current_outlet_id: string | null; requested_outlet_id: string;
  reason: string | null; status: string;
  reviewer_notes: string | null; created_at: string;
};

function OutletRequestsTab({
  orgId, canEdit, journalistById, outletById,
}: {
  orgId: string | null;
  canEdit: boolean;
  journalistById: Map<string, Journalist>;
  outletById: Map<string, Outlet>;
}) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState<Record<string, string>>({});

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["outlet-change-requests", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("outlet_change_requests" as any)
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false }) as any);
      if (error) throw error;
      return (data ?? []) as OutletChangeRequest[];
    },
  });

  const decide = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "approved" | "rejected" }) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await (supabase.from("outlet_change_requests" as any)
        .update({
          status,
          reviewed_by: userData.user?.id ?? null,
          reviewer_notes: notes[id]?.trim() || null,
        })
        .eq("id", id) as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Request updated");
      qc.invalidateQueries({ queryKey: ["outlet-change-requests", orgId] });
      qc.invalidateQueries({ queryKey: ["outlet-change-pending-count", orgId] });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const pending = requests.filter(r => r.status === "pending");
  const resolved = requests.filter(r => r.status !== "pending");

  if (isLoading) return <p className="text-justify text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold flex items-center gap-2"><ArrowRightLeft className="w-4 h-4" /> Outlet change requests</h3>
        <p className="text-justify text-sm text-muted-foreground mt-0.5">
          Journalists moving between organisations submit these from their portal. Approving one updates their
          registry record and affiliation immediately.
        </p>
      </div>

      {pending.length === 0 ? (
        <Card><CardContent className="pt-6 text-center text-muted-foreground">No pending outlet change requests.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {pending.map(r => {
            const j = journalistById.get(r.journalist_id);
            const from = r.current_outlet_id ? outletById.get(r.current_outlet_id)?.name : "—";
            const to = outletById.get(r.requested_outlet_id)?.name ?? "—";
            return (
              <Card key={r.id}>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-sm">{j ? `${j.first_name} ${j.last_name}` : "Unknown journalist"}</p>
                      <p className="text-justify text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5">
                        {from} <ArrowRightLeft className="w-3 h-3" /> <span className="font-medium text-foreground">{to}</span>
                      </p>
                      {r.reason && <p className="text-justify text-xs text-muted-foreground mt-1.5">"{r.reason}"</p>}
                      <p className="text-justify text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {new Date(r.created_at).toLocaleString()}
                      </p>
                    </div>
                    <Badge className="text-[10px] bg-amber-100 text-amber-700">Pending</Badge>
                  </div>
                  {canEdit && (
                    <div className="space-y-2 pt-2 border-t border-border">
                      <Input
                        placeholder="Reviewer notes (optional)"
                        value={notes[r.id] ?? ""}
                        onChange={(e) => setNotes(n => ({ ...n, [r.id]: e.target.value }))}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" disabled={decide.isPending} onClick={() => decide.mutate({ id: r.id, status: "approved" })}>
                          <Check className="w-3.5 h-3.5 mr-1.5" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" disabled={decide.isPending} onClick={() => decide.mutate({ id: r.id, status: "rejected" })}>
                          <X className="w-3.5 h-3.5 mr-1.5" /> Reject
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {resolved.length > 0 && (
        <div className="space-y-2">
          <p className="text-justify text-xs font-medium text-muted-foreground pt-2">History</p>
          {resolved.map(r => {
            const j = journalistById.get(r.journalist_id);
            const to = outletById.get(r.requested_outlet_id)?.name ?? "—";
            return (
              <div key={r.id} className="flex items-center justify-between text-sm py-2 border-b last:border-b-0">
                <span>{j ? `${j.first_name} ${j.last_name}` : "Unknown"} → {to}</span>
                <Badge variant={r.status === "approved" ? "default" : "destructive"} className="text-[10px] capitalize">{r.status}</Badge>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Field({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <Label className="text-xs text-muted-foreground mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

// =====================================================================
// Discover Tab, surfaces journalists from orgs with open_coverage=true
// =====================================================================

type DiscoveredJournalist = {
  journalist_id: string
  full_name: string
  slug: string | null
  headline: string | null
  avatar_url: string | null
  beats: string[]
  nationality: string | null
  score: number
  badge_tier: 'gold' | 'silver' | 'bronze' | 'none'
  org_name: string
  org_id: string
  is_accredited: boolean
}

const BADGE_COLORS: Record<string, string> = {
  gold:   'bg-amber-100 text-amber-700 border-amber-300',
  silver: 'bg-gray-100 text-gray-600 border-gray-300',
  bronze: 'bg-orange-100 text-orange-700 border-orange-300',
  none:   'bg-muted text-muted-foreground border-border',
}

const BADGE_ICONS: Record<string, React.ReactNode> = {
  gold:   <Trophy className="w-3 h-3" />,
  silver: <Star className="w-3 h-3" />,
  bronze: <Star className="w-3 h-3" />,
  none:   null,
}

function DiscoverTab({ canEdit, orgId, outlets, onImported }: { canEdit: boolean; orgId: string | null; outlets: Outlet[]; onImported: () => void }) {
  const [beat, setBeat]       = useState('')
  const [country, setCountry] = useState('')
  const [minScore, setMinScore] = useState('0')
  const [search, setSearch]   = useState('')
  const [importOutletId, setImportOutletId] = useState('')

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['discover-journalists', beat, country, minScore],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc('search_open_journalists', {
        p_beat:      beat      || null,
        p_country:   country   || null,
        p_min_score: parseInt(minScore, 10) || 0,
        p_limit:     60,
        p_offset:    0,
      } as any) as any)
      if (error) throw error
      return (data ?? []) as DiscoveredJournalist[]
    },
    staleTime: 60_000,
  })

  const importMutation = useMutation({
    mutationFn: async (j: DiscoveredJournalist) => {
      if (!orgId) throw new Error('No organisation')
      if (!importOutletId) throw new Error('Select an outlet to assign the journalist to first')
      const { error } = await (supabase.from('journalists' as any).insert({
        org_id:      orgId,
        first_name:  j.full_name.split(' ')[0] ?? j.full_name,
        last_name:   j.full_name.split(' ').slice(1).join(' ') || '',
        beats:       j.beats,
        nationality: j.nationality,
        status:      'active',
        gender:      'undisclosed',
        seniority:   'reporter',
        languages:   ['en'],
        consent_marketing: false,
        outlet_id:   importOutletId,
      }) as any)
      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Journalist added to your registry')
      onImported()
    },
    onError: (err: any) => toast.error(err.message),
  })

  const filtered = results.filter(j =>
    !search ||
    j.full_name.toLowerCase().includes(search.toLowerCase()) ||
    j.org_name.toLowerCase().includes(search.toLowerCase()) ||
    j.beats.some(b => b.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold">Journalist Discovery</h3>
        <p className="text-justify text-sm text-muted-foreground mt-0.5">
          Browse journalists from organisations with open coverage profiles.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search name, beat, organisation…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Input
          className="w-36"
          placeholder="Beat (e.g. health)"
          value={beat}
          onChange={e => setBeat(e.target.value)}
          onBlur={() => {}}
        />
        <Input
          className="w-36"
          placeholder="Nationality"
          value={country}
          onChange={e => setCountry(e.target.value)}
        />
        <Select value={minScore} onValueChange={setMinScore}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Min score" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Any score</SelectItem>
            <SelectItem value="400">400+ (Bronze)</SelectItem>
            <SelectItem value="600">600+ (Silver)</SelectItem>
            <SelectItem value="800">800+ (Gold)</SelectItem>
          </SelectContent>
        </Select>
        {canEdit && (
          <Select value={importOutletId} onValueChange={setImportOutletId}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Assign to outlet…" />
            </SelectTrigger>
            <SelectContent>
              {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      {canEdit && !importOutletId && (
        <p className="text-justify text-xs text-muted-foreground">Every journalist must belong to an outlet — pick one above before adding imports to your registry.</p>
      )}

      {/* Results */}
      {isFetching ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Searching…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center">
          No journalists found. Try adjusting your filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(j => (
            <Card key={j.journalist_id} className="flex flex-col">
              <CardContent className="pt-4 flex-1 space-y-2">
                {/* Header row */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {j.avatar_url ? (
                      <img
                        src={j.avatar_url}
                        alt={j.full_name}
                        className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <UserRound className="w-4 h-4 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{j.full_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{j.org_name}</p>
                    </div>
                  </div>
                  {j.badge_tier !== 'none' && (
                    <span className={`flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border flex-shrink-0 ${BADGE_COLORS[j.badge_tier]}`}>
                      {BADGE_ICONS[j.badge_tier]}
                      {j.badge_tier.charAt(0).toUpperCase() + j.badge_tier.slice(1)}
                    </span>
                  )}
                </div>

                {j.headline && (
                  <p className="text-justify text-xs text-muted-foreground line-clamp-2">{j.headline}</p>
                )}

                {/* Beats */}
                {j.beats.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {j.beats.slice(0, 4).map(b => (
                      <Badge key={b} variant="secondary" className="text-[10px] px-1.5 py-0">
                        {b}
                      </Badge>
                    ))}
                    {j.beats.length > 4 && (
                      <span className="text-[10px] text-muted-foreground">+{j.beats.length - 4}</span>
                    )}
                  </div>
                )}

                {/* Meta row */}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Globe className="w-3 h-3" />
                    {j.nationality ?? ''}
                  </span>
                  <span>Score: <strong className="text-foreground">{Math.round(j.score)}</strong></span>
                </div>
              </CardContent>

              {canEdit && (
                <div className="px-4 pb-3">
                  {j.is_accredited ? (
                    <Badge variant="default" className="text-xs w-full justify-center py-1">
                      In your registry
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full text-xs"
                      disabled={importMutation.isPending || !importOutletId}
                      onClick={() => importMutation.mutate(j)}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add to Registry
                    </Button>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
